import { z } from 'zod';

/**
 * Personen zoals de API ze over de lijn stuurt. Elk schema volgt één API
 * Resource (tussen haakjes); de schermen zien alleen het model uit `model.ts`,
 * `account.ts` en `platform.ts`.
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/* -------------------------------------------------------------------------- */
/* Het team van dit bedrijf                                                    */
/* -------------------------------------------------------------------------- */

/** (`Identity\UserResource.membership`) wat dít bedrijf van iemand weet. */
export const userMembershipResourceSchema = z.object({
    status: z.string(),
    status_label: z.string(),
    job_title: z.string().nullable(),
    employment_type: z.string(),
    response_time_minutes: z.number().int().nullable(),
    joined_at: timestamp.nullable(),
    last_active_at: timestamp.nullable(),
});

/**
 * (`Identity\UserResource`) een collega, zoals één keuringsbedrijf hem ziet. Nooit
 * met de andere bedrijven waar hij werkt: die staan alleen in platformbeheer.
 */
export const userResourceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    /** Een link; ontbreekt zolang de API alleen het bestand kent. */
    avatar_url: z.string().nullable().optional(),
    status: z.string(),
    is_platform_admin: z.boolean(),
    last_active_at: timestamp.nullable(),
    membership: userMembershipResourceSchema.nullable(),
    /** De rollen in dit bedrijf. */
    roles: z.array(z.string()).optional(),
});

export type UserResource = z.infer<typeof userResourceSchema>;

/** (`Identity\InvitationResource`) een uitnodiging die nog openstaat. */
export const invitationResourceSchema = z.object({
    uuid: z.string(),
    email: z.string(),
    /** Hoe de uitnodiger de collega kent; leeg als hij geen naam gaf. */
    name: z.string().nullable().optional(),
    role: z.string(),
    role_label: z.string(),
    invited_by: z.object({ uuid: z.string(), name: z.string() }).nullable().optional(),
    expires_at: timestamp,
    accepted_at: timestamp.nullable(),
    is_expired: z.boolean(),
    is_pending: z.boolean(),
    created_at: timestamp.nullable(),
});

export type InvitationResource = z.infer<typeof invitationResourceSchema>;

/* -------------------------------------------------------------------------- */
/* Het eigen account                                                           */
/* -------------------------------------------------------------------------- */

/** (`Identity\DeviceResource`) een apparaat met toegang. */
export const deviceResourceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    platform: z.string(),
    platform_label: z.string(),
    is_field_device: z.boolean(),
    os_version: z.string().nullable(),
    app_version: z.string().nullable(),
    owner: z.object({ uuid: z.string(), name: z.string() }),
    last_seen_at: timestamp.nullable(),
    last_sync_at: timestamp.nullable(),
    trusted_until: timestamp.nullable(),
    revoked_at: timestamp.nullable(),
    is_trusted: z.boolean(),
    is_revoked: z.boolean(),
    is_current: z.boolean(),
});

export type DeviceResource = z.infer<typeof deviceResourceSchema>;

/** (`ApiResponse::message`) een bevestiging in gewone taal. */
export const messageResourceSchema = z.object({ message: z.string() });

/* -------------------------------------------------------------------------- */
/* Platformbeheer                                                              */
/* -------------------------------------------------------------------------- */

/** (`Platform\PlatformUserResource.memberships[]`) */
export const platformMembershipResourceSchema = z.object({
    tenant_uuid: z.string(),
    tenant_name: z.string(),
    role: z.string().nullable(),
    status: z.string(),
    joined_at: timestamp.nullable(),
});

export type PlatformMembershipResource = z.infer<typeof platformMembershipResourceSchema>;

/** (`Platform\PlatformUserResource`) één persoon met al zijn lidmaatschappen. */
export const platformUserResourceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    is_platform_admin: z.boolean(),
    last_login_at: timestamp.nullable(),
    created_at: timestamp.nullable(),
    memberships: z.array(platformMembershipResourceSchema),
});

export type PlatformUserResource = z.infer<typeof platformUserResourceSchema>;
